import { useMemo, type ReactNode } from "react";
import {
  getObservabilityDatabase,
  type DatabaseMetricsResp,
  type DatabasePoint,
  type MetricWindow,
} from "@/api";
import { ChartCard, StatTile } from "@/components/metric";
import { MetricLineChart } from "./charts";
import { ObservabilityStateBlock } from "./ObservabilityStateBlock";
import {
  describeMetricUnit,
  describeSeriesCoverage,
  EMPTY_TOKEN,
  formatBytesValue,
  formatCount,
  formatSeconds,
  formatTimes,
  lastNonNullPoint,
  toSeries,
  type MetricFormatter,
} from "./observabilityModel";
import { useObservabilityMetric } from "./useObservabilityMetric";
import styles from "./ObservabilityPage.module.css";
import { formatDateTime } from "@/utils/datetime";

/**
 * 전역 DB 지표다. 창 요약 타일과 시계열 두 개를 표시한다.
 *
 * 타일은 **창 요약**(`MetricUnit`)에서만 값을 얻는다. 아래 차트의 점으로 평균을 내면 창이 아니라
 * "마지막 500점"의 평균이 된다. 30일 창에서는 두 값이 수십 배 차이 난다. 그래서 차트 부제는
 * 그 차트가 실제로 포함하는 구간을 따로 표시한다.
 */
export function ObservabilityDatabasePanel({ window }: { window: MetricWindow }): ReactNode {
  const { state, reload } = useObservabilityMetric(getObservabilityDatabase, window);
  return (
    <ObservabilityStateBlock
      state={state}
      loadingText="DB 지표를 불러오고 있습니다."
      onRetry={reload}
    >
      {(data) => <DatabaseBody data={data} window={window} />}
    </ObservabilityStateBlock>
  );
}

function DatabaseBody({
  data,
  window,
}: {
  data: DatabaseMetricsResp;
  window: MetricWindow;
}): ReactNode {
  const coverage = useMemo(
    () => describeSeriesCoverage(data.points, window),
    [data.points, window],
  );

  const sessionSeries = useMemo(
    () => [
      toSeries(data.points, {
        key: "active",
        label: "활성",
        slot: 0,
        pick: (point) => point.active_sessions,
      }),
      toSeries(data.points, {
        key: "idleInTransaction",
        label: "트랜잭션 안 유휴",
        slot: 1,
        pick: (point) => point.idle_in_transaction_sessions,
      }),
      toSeries(data.points, {
        key: "waiting",
        label: "대기",
        slot: 2,
        pick: (point) => point.waiting_sessions,
      }),
    ],
    [data.points],
  );

  // 롤백을 커밋과 같은 차트 축에 둔다. 이 스택에서 롤백은 실패 신호가 아니다. 커넥션 풀의
  // pre-ping 은 커넥션을 꺼낼 때마다 검증 문장을 트랜잭션 안에서 실행한다. 커밋할 것이 없어
  // ROLLBACK 으로 닫는다. 그래서 롤백 수는 커밋 수와 거의 1:1 이다(prod 실측 0.94~0.95).
  // 분모인 커밋 없이 롤백만 그리면 평상시 수치가 사고처럼 읽힌다.
  const throughputSeries = useMemo(
    () => [
      toSeries(data.points, {
        key: "commits",
        label: "커밋",
        slot: 3,
        pick: (point) => point.commits_delta,
      }),
      toSeries(data.points, {
        key: "rollbacks",
        label: "롤백",
        slot: 4,
        pick: (point) => point.rollbacks_delta,
      }),
    ],
    [data.points],
  );

  // 교착은 따로 그린다. 커밋·롤백은 네 자릿수이고 교착은 한 자릿수다. 한 차트 축에 두면
  // **교착 1건이 바닥선에 붙어 보이지 않는다**. 그러나 조치가 필요한 것은 그 1건이다.
  const deadlockSeries = useMemo(
    () => [
      toSeries(data.points, {
        key: "deadlocks",
        label: "교착",
        slot: 5,
        pick: (point) => point.deadlocks_delta,
      }),
    ],
    [data.points],
  );

  // 읽기 증폭은 읽은 행과 반환한 행의 비다. 값 하나로 줄이지 않고 추세로 표시한다. `8.9×`
  // 하나로는 "이 값이 나쁜가" 를 판단할 수 없기 때문이다. 이 차트가 답하는 질문은 **바뀌었나** 다.
  const readAmplificationSeries = useMemo(
    () => [
      toSeries(data.points, {
        key: "readAmplification",
        label: "읽기 증폭",
        slot: 6,
        pick: readAmplification,
      }),
    ],
    [data.points],
  );

  // DB 크기에는 창 요약 필드가 없다. 최신 점 하나가 유일한 근거라서 라벨이 그 사실을 표시한다.
  const latestSize = lastNonNullPoint(data.points, (point) => point.database_size_bytes);

  return (
    <div className={styles.panel}>
      <div className={styles.tiles}>
        <MetricStat label="활성 DB 세션" unit={data.active_sessions} format={formatCount} />
        <MetricStat
          label={connectionsLabel(data.max_connections)}
          unit={data.connections}
          format={formatCount}
        />
        {/* 정렬·해시가 work_mem 을 넘어 디스크에 쓴 양이다. 읽기 경로의 문제는 시간이 아니라
            이 버퍼 값에서 보인다(backend `docs/rules.md` §13). 대기 세션 타일은 두지 않는다.
            2,159 tick 을 측정했을 때 전부 0 이었다. 5분 간격 점 샘플은 1초 미만 락 대기를
            구조적으로 볼 수 없다. 경합은 아래 교착 차트가 누적 카운터로 보여준다. */}
        <MetricStat
          label="temp spill (tick 당)"
          unit={data.temp_bytes_delta}
          format={formatBytesValue}
        />
        {/* 이 타일만 큰 수가 최댓값이다. 「최장」은 max 를 묻는다. 최댓값들의 평균은 실제 어떤
            트랜잭션도 가리키지 않는다. 이 지표는 대부분의 tick 이 0 이라 평균은 `0s` 가 되고,
            "그런 일이 없었다" 로 읽힌다. 실제로는 같은 창에 24시간짜리가 있었다. */}
        <MetricStat
          label="최장 트랜잭션"
          unit={data.oldest_transaction_seconds}
          format={formatSeconds}
          headline="peak"
        />
        {/* 증분 지표다. 창 전체 합이 아니라 tick 하나의 값이다. 라벨이 그 단위를 표시한다. */}
        <MetricStat label="커밋 (tick 당)" unit={data.commits_delta} format={formatCount} />
        <MetricStat label="WAL (tick 당)" unit={data.wal_bytes_delta} format={formatBytesValue} />
        <StatTile
          label="DB 크기 (최근 스냅샷)"
          value={latestSize === null ? EMPTY_TOKEN : formatBytesValue(latestSize.value)}
          hint={latestSize === null ? undefined : `측정 ${formatDateTime(latestSize.atMs)}`}
          hintPlacement="below"
        />
      </div>

      <ChartCard
        title="DB 세션"
        subtitle={coverage.caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={sessionSeries}
          formatTick={formatDateTime}
          formatValue={formatCount}
          ariaLabel="DB 세션 상태별 추이"
        />
      </ChartCard>

      <ChartCard
        title="커밋·롤백 (tick 당)"
        subtitle={coverage.caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={throughputSeries}
          formatTick={formatDateTime}
          formatValue={formatCount}
          ariaLabel="커밋·롤백 추이"
        />
      </ChartCard>

      <ChartCard
        title="읽기 증폭 (tick 당)"
        subtitle={coverage.caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={readAmplificationSeries}
          formatTick={formatDateTime}
          formatValue={formatTimes}
          ariaLabel="읽기 증폭 추이"
        />
      </ChartCard>

      <ChartCard
        title="교착 (tick 당)"
        subtitle={coverage.caption}
        empty={coverage.isEmpty ? "이 구간에 표본이 없습니다." : undefined}
      >
        <MetricLineChart
          series={deadlockSeries}
          formatTick={formatDateTime}
          formatValue={formatCount}
          ariaLabel="교착 추이"
        />
      </ChartCard>
    </div>
  );
}

/**
 * 한 tick 의 읽기 증폭이다. 읽은 행 ÷ 반환한 행이다.
 *
 * 분모가 0 이면 비율이 성립하지 않으므로 **비운다**(0 이 아니다). 배포 직후 첫 tick 은
 * 비교할 직전 값이 없어 두 값이 모두 없다. 이 경우도 같이 비운다.
 */
function readAmplification(point: DatabasePoint): number | null {
  const returned = point.tup_returned_delta;
  const fetched = point.tup_fetched_delta;
  if (returned == null || fetched == null || fetched === 0) {
    return null;
  }
  return returned / fetched;
}

function MetricStat({
  label,
  unit,
  format,
  headline,
}: {
  label: string;
  unit: Parameters<typeof describeMetricUnit>[0];
  format: MetricFormatter;
  /** 큰 수로 최댓값을 표시할지 정한다. 이름 자체가 max 를 묻는 지표만 준다. */
  headline?: "mean" | "peak";
}): ReactNode {
  const view = describeMetricUnit(unit, format, { headline });
  return (
    <StatTile
      label={label}
      value={view.value}
      hint={view.hint === "" ? undefined : view.hint}
      // 일곱 타일 모두 봉우리·산포를 표시하므로 보조 문구가 한 줄에 들어가지 않는다. 그래서 옆이
      // 아니라 아래에 둔다.
      hintPlacement="below"
    />
  );
}

/**
 * 연결 수 라벨이다. 라벨이 한도를 표시한다.
 *
 * 점유만 보이면 "21 이 많은가" 를 판단할 수 없다. 한도는 설정값이라 창 안에서 상수다. 그래서
 * 값·최대·산포에 매번 붙이지 않고 라벨에 한 번만 적는다. 한도를 보내지 않는 구 BE 응답에서는
 * 없는 수를 만들지 않고 라벨만 표시한다. 런타임 탭의 `upstreamPoolLabel` 과 같은 규칙이다.
 */
function connectionsLabel(maximum: number | null): string {
  return maximum == null ? "연결 수" : `연결 수 (한도 ${formatCount(maximum)})`;
}
